/**
 * One client's billing run, as the six steps it does them in.
 *
 * `.docs/20-studio-portal-pages.md` §Before fan-out 3 names it, and
 * `mockups/studio/money.html` §"Money in" is where it is read: the run table says
 * what each client was billed, and this says what the run *did* to get there —
 * which is the only thing a person looks at before pressing `Re-run`.
 *
 * **The step list is `BILLING_RUN_STEPS`, not the row's own.** `BillingRunSchema`
 * carries "one entry per step the run reached", so a run that failed at `extras` has
 * four entries and the stepper still has to show the two it never got to. Iterating
 * the published list and looking each one up is what makes a partial run legible;
 * iterating `run.steps` would draw a complete-looking stepper over an incomplete run.
 *
 * The notes are underneath rather than on the steps. `Step` takes `Common` — ARIA and
 * an id — so there is nowhere on it to hang "2 usage month(s) closed, 2 billable",
 * and that sentence is the reason somebody opened this. `Kv` is where it goes.
 */

import { BILLING_RUN_STEPS, type BillingRunView } from '@fareast/shared'

import { Kv, KvRow } from '../portal/Kv.js'
import { Stack } from '../portal/Layout.js'
import { Step, Stepper } from '../portal/Stepper.js'
import { Muted, Small } from '../portal/Text.js'

/**
 * What each step is called on screen.
 *
 * Written out rather than de-slugged from the name: `period_end` is "Retainers
 * ending" to a reader and "period_end" only to the run, and `changes` on its own
 * means nothing at all.
 */
const STEP_LABELS: Readonly<Record<(typeof BILLING_RUN_STEPS)[number], string>> = {
  period_end: 'Retainers ending',
  changes: 'Package changes',
  usage: 'Usage',
  extras: 'Extras',
  retainer: 'Retainer',
  invoice: 'Invoice',
}

export type RunStepperProps = {
  readonly run: BillingRunView
}

export function RunStepper({ run }: RunStepperProps) {
  const reached = new Map(run.steps.map((step) => [step.step, step]))

  /*
   * Which step is "now": the first one this run has not finished, and only while it
   * is still running. A `done` run has no current step — every `Step` on it is
   * either done or skipped — and a `failed` one stopped somewhere, which is what the
   * error sentence beside the stepper says rather than a step pretending to be live.
   */
  const current =
    run.status === 'running'
      ? BILLING_RUN_STEPS.find((name) => (reached.get(name)?.state ?? 'pending') === 'pending')
      : undefined

  const notes = BILLING_RUN_STEPS.map((name) => ({ name, step: reached.get(name) })).filter(
    (one) => one.step !== undefined && one.step.note !== '',
  )

  return (
    <Stack gap="tight">
      <Stepper>
        {BILLING_RUN_STEPS.map((name) => {
          const state = reached.get(name)?.state ?? 'pending'
          const label = STEP_LABELS[name]
          return (
            <Step
              key={name}
              state={state === 'done' ? 'done' : name === current ? 'now' : undefined}
              // The word the class does not carry. `step--done` is a weight, and
              // "skipped" and "not reached" look identical without this.
              aria-label={`${label} — ${state === 'pending' && name !== current ? 'not reached' : state}`}
            >
              {label}
            </Step>
          )
        })}
      </Stepper>

      {notes.length === 0 ? null : (
        <Kv bare>
          {notes.map(({ name, step }) => (
            <KvRow
              key={name}
              k={STEP_LABELS[name]}
              v={
                <Small>
                  {step?.state === 'skipped' ? <Muted>skipped — </Muted> : null}
                  {step?.note}
                </Small>
              }
            />
          ))}
        </Kv>
      )}
    </Stack>
  )
}
